import React, { useContext, useEffect, useState } from 'react';
import styles from './style.module.scss';
import { Tab } from '@/globalComponents/tab';
import { TabItemText } from '@/globalComponents/tabItemText';
import { AmcDetails } from './amcDetails';
import { Button } from '@/globalComponents/button';
import { ProductContext } from '@/context/productContext';
import { BenefitsType } from '@/types/tier';
import { converToAMCServiceDetails, getStatus } from './utils';
import ProductUtils from '@/utils/productUtils';
import { AMCServiceDetails, AMCServiceDetailsItemType } from './amcServiceDetails';
import { fetchAmcServiceDetails } from '@/actions/fetchAmcServiceDetails';
import Loader from '@/globalComponents/loader';

export type UpcomingPlanType = {
  startDate: string;
  endDate: string;
  planType: string;
  benefits: BenefitsType[];
  perYearPrice: number;
};

interface AvailablePlansProps {
  onClickBookAServiceButton: () => void;
  upcomingPlan: UpcomingPlanType | null;
  existingPlan: UpcomingPlanType | null;
}

export const AvailablePlans = (props: AvailablePlansProps) => {
  const { onClickBookAServiceButton, upcomingPlan, existingPlan } = props;

  const isUpcomingPlanAvailable: boolean = !!(upcomingPlan && upcomingPlan.planType);

  const [activeTab, setActiveTab] = useState('existingPlan');
  const [serviceDetailsList, setServiceDetailsList] = useState<AMCServiceDetailsItemType[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  const [invoiceLink, setInvoiceLink] = useState<string | null>('');
  const [orderPricePerUnit, setOrderPricePerUnit] = useState<number | undefined>(NaN);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    state: { selectedProduct, isLoading: isPageLevelLoaderActive },
    dispatch: { setAmcServiceDetails },
  } = useContext(ProductContext);

  const { agreementId } =
    activeTab === 'existingPlan' ? selectedProduct.currentPlan : selectedProduct.upcomingAMCPlan;

  const statusOfExistingPlanProduct = new ProductUtils(selectedProduct).getProductLabel();
  const showExisitingPlan: boolean = !!(
    statusOfExistingPlanProduct && activeTab === 'existingPlan'
  );
  const existingTabText =
    statusOfExistingPlanProduct === 'In Warranty' ||
    statusOfExistingPlanProduct === 'Warranty Expiring'
      ? 'Existing Warranty'
      : 'Existing AMC';

  useEffect(() => {
    const getServiceDetails = async () => {
      if (selectedProduct && statusOfExistingPlanProduct !== '') {
        setIsLoading(true);
        setIsError(false);
        try {
          const response = await fetchAmcServiceDetails(selectedProduct.id, agreementId);
          const { invoiceLink, orderPricePerUnit, mandatoryServices } = response;
          setInvoiceLink(invoiceLink);
          setOrderPricePerUnit(orderPricePerUnit);
          setServiceDetailsList(converToAMCServiceDetails(mandatoryServices));
          setAmcServiceDetails(response);
        } catch (error) {
          setIsError(true);
          setInvoiceLink(null);
          setOrderPricePerUnit(0);
        } finally {
          setIsLoading(false);
        }
      }
    };
    getServiceDetails();
  }, [selectedProduct, agreementId]);

  useEffect(() => {
    setActiveTab('existingPlan');
  }, [selectedProduct]);

  if (!existingPlan?.planType || isPageLevelLoaderActive) {
    return <></>;
  }

  const renderComponent = () => {
    if (isLoading && !isPageLevelLoaderActive) {
      return (
        <div className="grid place-content-center w-full md:h-[250px] h-[200px]">
          <Loader size="large" />
        </div>
      );
    }
    return (
      <>
        {showExisitingPlan ? (
          <AmcDetails
            status={getStatus(statusOfExistingPlanProduct)}
            {...{ ...existingPlan }}
            invoiceLink={invoiceLink}
            orderPricePerUnit={orderPricePerUnit}
          />
        ) : (
          isUpcomingPlanAvailable &&
          upcomingPlan && (
            <AmcDetails
              {...{ ...upcomingPlan }}
              invoiceLink={invoiceLink}
              orderPricePerUnit={orderPricePerUnit}
              status={'AMC Tier'}
            />
          )
        )}
        <div className="md:mt-8 mt-6">
          <AMCServiceDetails
            data={serviceDetailsList}
            isError={isError}
            statusOfDevice={
              activeTab === 'existingPlan' ? getStatus(statusOfExistingPlanProduct) : 'AMC'
            }
          />
        </div>
      </>
    );
  };

  return (
    <div className="mt-1 flex flex-col ">
      {isUpcomingPlanAvailable && (
        <div
          className={`flex flex-row  items-center ${styles.tabContainer} md:mb-8 mb-6`}
          data-testid="available-plans-tab"
        >
          <Tab
            tabItems={[
              {
                id: 'existingPlan',
                component: (
                  <TabItemText
                    text={existingTabText}
                    classNames={styles.inActiveTabItem}
                  ></TabItemText>
                ),
              },
              {
                id: 'upcomingPlan',
                component: (
                  <TabItemText
                    text={'Upcoming AMC'}
                    classNames={styles.inActiveTabItem}
                  ></TabItemText>
                ),
              },
            ]}
            activeClassNames={`${styles.activeTabItem}`}
            classNames={`${styles.tab} inline`}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
          />
          <Button
            classNames={`${styles.bookAServiceButton} ${styles.webButtonContainer}`}
            variant="primary"
            onClick={onClickBookAServiceButton}
            dataTestId="book-service"
          >
            BOOK A SERVICE
          </Button>
        </div>
      )}
      {renderComponent()}
    </div>
  );
};
